/**
 * The design flow's state as the backend recorded it, rather than as this tab
 * happened to observe it.
 *
 * Everything the flow produces is already persisted: the context bundle and its
 * plots on the `fim-sentinel-dctx-…` entry, the designed plan on that same entry
 * (written by the design job, because the job's own entry is scratch), and the
 * fit, its context copy and its evaluation on the run entry. Nothing here writes
 * or starts anything — it is the read side that a reload, a second tab or a
 * different machine all get to use, and it is why the SSE stream never has to be
 * re-attached.
 */

import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import { useGetRolodexEntriesByLoadOptions } from 'features/rolodex'
import { describeRequestError } from 'features/sentinelDesign/requestError'
import type {
  SentinelDesignContext,
  SentinelDesignContextBuilding,
  SentinelDesignResult,
  SentinelEvaluation,
  SentinelPlan,
} from 'features/sentinelDesign/types'
import {
  resolveRunState,
  SENTINEL_RUNNING_POLL_MS,
  SENTINEL_STALLED_AFTER_MS,
  type SentinelRunTiming,
} from 'features/sentinels/runState'
import {
  SENTINEL_ENTRY_TYPE,
  type SentinelEntryData,
  type SentinelLoadedEntry,
} from 'features/sentinels/types'
import { useMemo } from 'react'
import useSWR from 'swr'

type EntryFiles = SentinelEntryData['files']

type ContextRead = SentinelDesignContext | SentinelDesignContextBuilding

/**
 * The 202 answer, told apart from a bundle by the `status` a bundle never has.
 */
const buildingAnswer = (
  read: ContextRead | undefined
): SentinelDesignContextBuilding | undefined =>
  (read as SentinelDesignContextBuilding | undefined)?.status === 'building'
    ? (read as SentinelDesignContextBuilding)
    : undefined

/**
 * A build that has reported "building" for longer than the stall window has, in
 * all likelihood, had its job die (a preemption, an OOM, a hot-reload restart)
 * without getting to write its own error status — so nothing on the backend
 * will ever flip it. Elapsed time is the only signal, exactly as for a stalled
 * run, and past it the UI stops polling forever and says so.
 */
const isStalledBuild = (
  building: SentinelDesignContextBuilding | undefined
): boolean => {
  if (!building) return false
  const started = Date.parse(building.requested_at ?? '')
  return (
    Number.isFinite(started) && Date.now() - started > SENTINEL_STALLED_AFTER_MS
  )
}

/**
 * A context bundle read back off its entry, for a design this tab did not build.
 *
 * Building one costs a historian pull and a rendered plot per tag, so a page
 * that returns to a design already paid for — a reload, a second tab, someone
 * following a link — resumes from the entry rather than rebuilding it. Null
 * `contextId` is the ordinary "starting fresh" case and fetches nothing.
 *
 * A build still in flight comes back as `building` rather than as an error, and
 * is re-read until it lands: the id is parked in the URL as soon as the build
 * starts, so returning to one mid-build is expected rather than exceptional.
 */
export const useDesignContext = (
  siteId: string | null,
  contextId: string | undefined
) => {
  const { FIM_SERVICE_URL } = useRuntimeVariables()
  const { authorizedAxios } = useGeckoAuth()
  const { data, error, isLoading } = useSWR<ContextRead>(
    siteId && contextId ? ['sentinel-design-context', siteId, contextId] : null,
    async () => {
      const response = await authorizedAxios.get<ContextRead>(
        `${FIM_SERVICE_URL}/api/v1/sites/${encodeURIComponent(
          siteId as string
        )}/sentinel-design/context/${encodeURIComponent(contextId as string)}`
      )
      return response.data
    },
    {
      revalidateOnFocus: false,
      shouldRetryOnError: false,
      refreshInterval: (latest) => {
        const b = buildingAnswer(latest)
        // Stop polling a stalled build: no one is coming to finish it.
        return b && !isStalledBuild(b) ? SENTINEL_RUNNING_POLL_MS : 0
      },
    }
  )
  const building = buildingAnswer(data)
  const stalled = isStalledBuild(building)
  return {
    context: building ? undefined : (data as SentinelDesignContext | undefined),
    // A stalled build is no longer "building"; it is a dead end the UI surfaces.
    building: stalled ? undefined : building,
    stalled,
    error: error === undefined ? undefined : describeRequestError(error),
    isLoading,
  }
}

const timingOf = (
  rows: SentinelLoadedEntry[] | undefined
): SentinelRunTiming => ({
  status: rows?.[0]?.entry?.data?.summary?.status,
  requestedAt: rows?.[0]?.entry?.data?.summary?.requested_at,
  updatedAt: rows?.[0]?.entry?.updated_at,
})

/**
 * One `fim-sentinel` entry by id, re-read while its job is in flight.
 *
 * Used for the design-context entry, which the sentinel list deliberately does
 * not carry. It re-polls on the same rule the list does, so a context build
 * started elsewhere lands here too.
 */
export const useSentinelEntry = (entryId: string | undefined) => {
  const { data, error, isLoading, mutate } = useGetRolodexEntriesByLoadOptions<
    SentinelLoadedEntry[]
  >(
    entryId
      ? { ids: [entryId], type: [SENTINEL_ENTRY_TYPE], limit: 1 }
      : undefined,
    {
      refreshInterval: (latest) =>
        resolveRunState(timingOf(latest)) === 'running'
          ? SENTINEL_RUNNING_POLL_MS
          : 0,
      refreshWhenHidden: false,
    }
  )
  return { entry: data?.[0]?.entry, error, isLoading, mutate }
}

/**
 * A JSON file attached to an entry, through Rolodex's signed-download hop.
 *
 * Keyed on the entry, the path AND a caller-supplied revision: a redesign
 * overwrites `plan.json` in place, so without the revision in the key the tab
 * that asked for the redesign would keep serving the plan it already had.
 */
const useEntryJson = <T>(
  entryId: string | undefined,
  path: string | undefined,
  revision: number | string = 0
) => {
  const { authorizedAxios } = useGeckoAuth()
  const { ROLODEX_URL } = useRuntimeVariables()
  const { data, error, isLoading } = useSWR<T>(
    entryId && path ? ['sentinel-entry-file', entryId, path, revision] : null,
    async () => {
      const signed = await authorizedAxios.get(
        `${ROLODEX_URL}/api/v2/files/${entryId}/${path}`
      )
      const download = await fetch(signed.data as string)
      if (!download.ok) {
        throw new Error(`${path} download failed (HTTP ${download.status})`)
      }
      return (await download.json()) as T
    },
    { revalidateOnFocus: false }
  )
  return { data, error, isLoading }
}

/**
 * What a run was dispatched with, as fim-service wrote it to `request.json`.
 *
 * The one artifact a failed run is guaranteed to have: it is written before the
 * job starts, whereas plan.json and result.json are written by the job at the
 * end. So it is where the failure view gets the plan that was attempted and the
 * context bundle to re-run from.
 */
export interface SentinelRunRequestFile {
  plan?: SentinelPlan
  context_id?: string
  context_entry_id?: string
  name?: string
  display_name?: string
  run_id?: string
  rationale?: Record<string, string>
  narrative?: string
}

export const useRunRequest = (entryId: string | undefined, files: EntryFiles) =>
  useEntryJson<SentinelRunRequestFile>(entryId, files?.request)

export interface DurableDesignArtifacts {
  /**
   * The persisted bundle. It is a superset of the `/context` response — the
   * same object, before `api_result` trimmed it — so it reads as one.
   */
  context?: SentinelDesignContext
  plan?: SentinelPlan
  /** The design call's commentary; present on context entries only. */
  design?: SentinelDesignResult
  evaluation?: SentinelEvaluation
  isLoading: boolean
}

/**
 * The design artifacts attached to one entry, whichever of them it has.
 *
 * A run entry gains its plan, context copy and evaluation only as the job
 * publishes them, so every field here is independently optional and a caller
 * falls back to the context entry for whatever is still missing.
 */
export const useDurableDesignArtifacts = (
  entryId: string | undefined,
  files: EntryFiles,
  revision: number | string = 0
): DurableDesignArtifacts => {
  const context = useEntryJson<SentinelDesignContext>(entryId, files?.context)
  const plan = useEntryJson<SentinelPlan>(entryId, files?.plan, revision)
  const design = useEntryJson<SentinelDesignResult>(
    entryId,
    files?.design,
    revision
  )
  const evaluation = useEntryJson<SentinelEvaluation>(
    entryId,
    files?.evaluation
  )

  return useMemo(
    () => ({
      context: context.data,
      plan: plan.data,
      design: design.data,
      evaluation: evaluation.data,
      isLoading:
        context.isLoading ||
        plan.isLoading ||
        design.isLoading ||
        evaluation.isLoading,
    }),
    [
      context.data,
      context.isLoading,
      plan.data,
      plan.isLoading,
      design.data,
      design.isLoading,
      evaluation.data,
      evaluation.isLoading,
    ]
  )
}
